import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { WorkflowExecutionInfoItem } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import {
  DEFAULT_REGION_CODE,
  DEFAULT_TABLE_PAGINATION,
  DEFAULT_WORKFLOW_CLIENT_TYPE,
} from "../constants/index";
import { WORKFLOW_CLIENT_TYPES } from "../constants/workflow-client-types";
import mainStyles from "../styles";
import { listWorkflowExecutions } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import ArchiveAlexandriaDocumentsForm from "components/workflows/archive-alexandria-documents-form";
import ArchiveDocumentsByOwnerForm from "components/workflows/archive-documents-by-owner-form";
import BulkAddUsersToGroupsForm from "components/workflows/bulk-add-users-to-groups-form";
import BulkChangeEmailAddressForm from "components/workflows/bulk-change-user-email-form";
import BulkCopyTemplatePermissionsForm from "components/workflows/bulk-copy-template-permissions-form";
import BulkMoveGRSForm from "components/workflows/bulk-move-grs-form";
import BulkRemoveBouncesForm from "components/workflows/bulk-remove-bounces-form";
import BulkTransferUsersForm from "components/workflows/bulk-transfer-users-form";
import BulkUpdateIdentityForm from "components/workflows/bulk-update-identity-form";
import ExecuteWorkflowByNameCsvForm from "components/workflows/execute-workflow-by-name-csv-form";
import ExecuteWorkflowByNameForm from "components/workflows/execute-workflow-by-name-form";
import ResetOrgTemplatesAccessForm from "components/workflows/reset-org-template-access-form";
import RestoreArchivedInspectionsForm from "components/workflows/restore-archived-inspections-form";
import RestoreDeletedInspectionsForm from "components/workflows/restore-deleted-inspections-form";
import UpdateDocumentContextForm from "components/workflows/update-document-context-form";
import UpdateInspectionTitlesForm from "components/workflows/update-inspection-titles-form";
import WorkflowRecord from "components/workflows/workflow-record";
import { SnackbarContext } from "context/snackbar-context";

const Workflows = () => {
  const classes = mainStyles();
  const [workflowChoice, setWorkflowChoice] = useState("");
  const [workflowByName, setWorkflowByName] = useState("");
  const [workflowClientType, setWorkflowClientType] = useState<string>(
    DEFAULT_WORKFLOW_CLIENT_TYPE,
  );
  // Only Integrations workflows are actually sharded by region today (each
  // api-tools-support pod only has visibility into its own region's
  // api-integrations namespace) - other client types ignore this. Region
  // travels with the client-type dropdown selection (see
  // WORKFLOW_CLIENT_TYPE_OPTIONS), rather than needing its own control.
  const [workflowRegion, setWorkflowRegion] =
    useState<string>(DEFAULT_REGION_CODE);
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useContext(SnackbarContext);
  const [workflowList, setWorkflowList] = useState<WorkflowExecutionInfoItem[]>(
    [],
  );
  const [nextPageToken, setNextPageToken] = useState<Uint8Array>();

  const shouldDisableShowMore =
    isLoading ||
    workflowList.length === 0 ||
    nextPageToken?.length === 0 ||
    workflowChoice === "";

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setWorkflowChoice(event.target.value);
  };

  const getSupportWorkflows = ({
    workflowType,
    nextPageToken,
    clearList,
    clientType = DEFAULT_WORKFLOW_CLIENT_TYPE,
    region,
  }: {
    workflowType: string;
    nextPageToken?: Uint8Array;
    clearList?: boolean;
    clientType?: string;
    // Explicit param rather than reading workflowRegion state directly -
    // callers that just learned a new region (e.g. from a client-type change)
    // would otherwise read the stale pre-update value, since setWorkflowRegion
    // doesn't apply until the next render.
    region?: string;
  }) => {
    const resolvedClientType = clientType || DEFAULT_WORKFLOW_CLIENT_TYPE;
    const resolvedRegion = region || workflowRegion;
    setWorkflowClientType(resolvedClientType);
    setWorkflowRegion(resolvedRegion);
    setIsLoading(true);

    const requestParams = {
      workflowType,
      pageSize: DEFAULT_TABLE_PAGINATION,
      nextPageToken: clearList ? new Uint8Array() : nextPageToken,
      clientType: resolvedClientType,
    };

    const headers = callOptionsHeaders(resolvedRegion);

    listWorkflowExecutions(requestParams, headers)
      .then((listResponse) => {
        const results = listResponse.workflowExecutions;
        const loadingInitialWorkflows = nextPageToken?.length === 0;

        const updatedResults = [
          ...(loadingInitialWorkflows || clearList ? [] : workflowList),
          ...results,
        ];
        setWorkflowList(updatedResults);
        setNextPageToken(listResponse.nextPageToken);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const displayWorkflowForm = (workflow: string) => {
    switch (workflow) {
      case "BulkUpdateInspectionTitle":
        return (
          <UpdateInspectionTitlesForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflow,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkRestoreInspections":
        return (
          <RestoreArchivedInspectionsForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "ArchiveAlexandriaDocuments":
        return (
          <ArchiveAlexandriaDocumentsForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "ArchiveDocumentByOwner":
        return (
          <ArchiveDocumentsByOwnerForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkRestoreDeletedInspections":
        return (
          <RestoreDeletedInspectionsForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkCopyTemplatePermissionAndAutoShare":
        return (
          <BulkCopyTemplatePermissionsForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "ExecuteWorkflowByName":
        return (
          <ExecuteWorkflowByNameForm
            onClientTypeChange={(clientType, region) => {
              setWorkflowClientType(clientType);
              setWorkflowRegion(region);
            }}
            onRefreshWorkflows={(
              workflowName: string,
              clientType: string,
              region: string,
            ) => {
              getSupportWorkflows({
                workflowType: workflowName,
                nextPageToken: new Uint8Array(),
                clientType: clientType || DEFAULT_WORKFLOW_CLIENT_TYPE,
                region,
              });
              setWorkflowByName(workflowName);
            }}
          />
        );
      case "ExecuteWorkflowByNameCSV":
        return (
          <ExecuteWorkflowByNameCsvForm
            onClientTypeChange={(clientType, region) => {
              setWorkflowClientType(clientType);
              setWorkflowRegion(region);
            }}
            onRefreshWorkflows={(
              workflowName: string,
              clientType: string,
              region: string,
            ) => {
              getSupportWorkflows({
                workflowType: workflowName,
                nextPageToken: new Uint8Array(),
                clientType: clientType || DEFAULT_WORKFLOW_CLIENT_TYPE,
                region,
              });
              setWorkflowByName(workflowName);
            }}
          />
        );
      case "BulkAddUsersToGroups":
        return (
          <BulkAddUsersToGroupsForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkTransferUsers":
        return (
          <BulkTransferUsersForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkChangeEmailAddress":
        return (
          <BulkChangeEmailAddressForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkUpdateIdentity":
        return (
          <BulkUpdateIdentityForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkMoveGRS":
        return (
          <BulkMoveGRSForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkUpdateContext":
        return (
          <UpdateDocumentContextForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "ResetOrgTemplatesAccess":
        return (
          <ResetOrgTemplatesAccessForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );
      case "BulkRemoveOrgUsersFromBounceList":
        return (
          <BulkRemoveBouncesForm
            listSupportWorkflows={() => {
              getSupportWorkflows({
                workflowType: workflowChoice,
                nextPageToken: new Uint8Array(),
              });
            }}
          />
        );

      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (workflowChoice) {
      getSupportWorkflows({
        workflowType: workflowChoice,
        nextPageToken: new Uint8Array(),
        clientType: DEFAULT_WORKFLOW_CLIENT_TYPE,
      });
    }
    // eslint-disable-next-line
  }, [workflowChoice]);

  const tableHeadersDisplay: string[] = [
    "Workflow ID",
    "Input/Result",
    "Start Time",
    "Close Time",
    "Status",
    "Progress",
    "Action",
  ];

  const displayTableHeaders = () => {
    if (tableHeadersDisplay.length === 0) {
      return;
    }

    return tableHeadersDisplay.map((header, index) => {
      return (
        <TableCell key={index} align="left">
          <b>{header}</b>
        </TableCell>
      );
    });
  };

  const displayWorkflowRecords = () => {
    if (workflowList.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={tableHeadersDisplay.length}>
            No records to display
          </TableCell>
        </TableRow>
      );
    }

    return workflowList.map((record) => {
      return (
        <>
          <WorkflowRecord
            record={record}
            isSubscriptionsWorkflow={
              workflowClientType === WORKFLOW_CLIENT_TYPES.SUBSCRIPTIONS_CLOUD
            }
            clientType={workflowClientType}
            refreshWorkflowsList={(clearList?: boolean) => {
              getSupportWorkflows({
                workflowType:
                  workflowChoice === "ExecuteWorkflowByName"
                    ? workflowByName
                    : workflowChoice,
                nextPageToken: new Uint8Array(),
                clearList,
                clientType: workflowClientType,
              });
            }}
          />
        </>
      );
    });
  };

  const onWorkflowSelection = (event: SelectChangeEvent<string>) => {
    setWorkflowList([]);
    setNextPageToken(new Uint8Array());
    handleSelectChange(event);
  };

  const workflowMenuItems: Array<{
    displayText: string;
    keyAttribute: string;
    valueAttribute: string;
    testDataIdAttribute: string;
  }> = [
    {
      displayText: "Update Inspection Title",
      keyAttribute: "updateInspectionTitle",
      valueAttribute: "BulkUpdateInspectionTitle",
      testDataIdAttribute: "item-bulk-update-inspection-title",
    },
    {
      displayText: "Update User's Inspection/Template Document Context",
      keyAttribute: "updateDocumentContext",
      valueAttribute: "BulkUpdateContext",
      testDataIdAttribute: "item-bulk-update-context",
    },

    {
      displayText: "Restore Archived Inspections",
      keyAttribute: "bulkRestoreInspections",
      valueAttribute: "BulkRestoreInspections",
      testDataIdAttribute: "item-bulk-restore-inspection",
    },
    {
      displayText: "Archive Inspections and Templates",
      keyAttribute: "archiveAlexandriaDocuments",
      valueAttribute: "ArchiveAlexandriaDocuments",
      testDataIdAttribute: "item-archive-alexandria-documents",
    },
    {
      displayText: "Bulk Change Users Email Addresses",
      keyAttribute: "bulkChangeEmailAddress",
      valueAttribute: "BulkChangeEmailAddress",
      testDataIdAttribute: "bulk-change-email-address",
    },
    {
      displayText: "Bulk Update Identity (Email and/or Username)",
      keyAttribute: "bulkUpdateIdentity",
      valueAttribute: "BulkUpdateIdentity",
      testDataIdAttribute: "bulk-update-identity",
    },
    {
      displayText: "Archive Inspections and Templates By Owner",
      keyAttribute: "archiveDocumentByOwner",
      valueAttribute: "ArchiveDocumentByOwner",
      testDataIdAttribute: "item-archive-documents-by-owner",
    },
    {
      displayText: "Restore Deleted Inspections",
      keyAttribute: "bulkRestoreDeletedInspections",
      valueAttribute: "BulkRestoreDeletedInspections",
      testDataIdAttribute: "item-bulk-restore-deleted-inspections",
    },
    {
      displayText: "Copy Template Permissions and Autoshares",
      keyAttribute: "bulkCopyTemplatePermissions",
      valueAttribute: "BulkCopyTemplatePermissionAndAutoShare",
      testDataIdAttribute: "item-bulk-copy-template-permissions",
    },
    {
      displayText: "Execute Workflow by Name",
      keyAttribute: "executeWorkflowByName",
      valueAttribute: "ExecuteWorkflowByName",
      testDataIdAttribute: "item-execute-workflow-by-name",
    },
    {
      displayText: "Execute Workflow by Name (CSV)",
      keyAttribute: "executeWorkflowByNameCsv",
      valueAttribute: "ExecuteWorkflowByNameCSV",
      testDataIdAttribute: "item-execute-workflow-by-name-csv",
    },
    {
      displayText: "Bulk Add Users to Groups",
      keyAttribute: "bulkAddUsersToGroups",
      valueAttribute: "BulkAddUsersToGroups",
      testDataIdAttribute: "item-bulk-add-users-to-groups",
    },
    {
      displayText: "Bulk Transfer Users with Their Documents",
      keyAttribute: "bulkTransferUsers",
      valueAttribute: "BulkTransferUsers",
      testDataIdAttribute: "item-bulk-transfer-users",
    },
    {
      displayText: "Bulk Transfer Global Response Sets",
      keyAttribute: "bulkMoveGRS",
      valueAttribute: "BulkMoveGRS",
      testDataIdAttribute: "item-bulk-move-grs",
    },
    {
      displayText: "Reset Org Templates Access",
      keyAttribute: "resetOrgTemplatesAccess",
      valueAttribute: "ResetOrgTemplatesAccess",
      testDataIdAttribute: "item-reset-org-templates-access",
    },
    {
      displayText: "Bulk Remove Org Users from SendGrid Bounce List",
      keyAttribute: "bulkRemoveBounces",
      valueAttribute: "BulkRemoveOrgUsersFromBounceList",
      testDataIdAttribute: "item-bulk-remove-bounces",
    },
  ];

  const displayWorkflowSelectItems = () => {
    if (workflowMenuItems.length === 0) {
      return;
    }

    return workflowMenuItems
      .sort((a, b) =>
        a.displayText > b.displayText
          ? 1
          : a.displayText < b.displayText
            ? -1
            : 0,
      )
      .map((item) => {
        return (
          <MenuItem
            key={item.keyAttribute}
            value={item.valueAttribute}
            data-testid={item.testDataIdAttribute}
          >
            {item.displayText}
          </MenuItem>
        );
      });
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography
            variant="h5"
            color="textPrimary"
            data-testid="workflows-title"
          >
            Workflows
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Box
            component="form"
            sx={{ ...classes.spaceSearch, marginTop: 1, marginBottom: 1 }}
            noValidate
            autoComplete="off"
          >
            <FormControl
              variant="outlined"
              size="small"
              sx={classes.formControl}
            >
              <InputLabel id="workflow-select-label">Workflow</InputLabel>
              <Select
                labelId="workflow-select-label"
                id="workflow-select"
                value={workflowChoice}
                label="Workflow"
                defaultValue=""
                onChange={(event) => onWorkflowSelection(event)}
                data-testid="workflow-select"
                inputProps={{ "data-testid": "workflow-select-item" }}
              >
                {displayWorkflowSelectItems()}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {displayWorkflowForm(workflowChoice)}

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table aria-label="workflow-table">
              <TableHead>
                <TableRow>{displayTableHeaders()}</TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={tableHeadersDisplay.length}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  displayWorkflowRecords()
                )}
              </TableBody>

              <TableFooter>
                <TableCell colSpan={tableHeadersDisplay.length}>
                  <Box display="flex" justifyContent="flex-end">
                    <Box>
                      <Button
                        color="primary"
                        size="small"
                        disabled={shouldDisableShowMore}
                        onClick={() => {
                          getSupportWorkflows({
                            workflowType:
                              workflowChoice === "ExecuteWorkflowByName"
                                ? workflowByName
                                : workflowChoice,
                            nextPageToken,
                            clientType: workflowClientType,
                          });
                        }}
                        data-testid="load-more-workflow-jobs-btn"
                      >
                        ...SEE MORE
                      </Button>
                    </Box>
                  </Box>
                </TableCell>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default Workflows;
